import { Tool, Web3DViewer } from '@technology/web3d';
import {
  LineEditingModel,
  LineEditingPlugin,
} from '@technology/web3d-plugin-line-editing';
import { DrawingTool, NO_ACTIVE_TOOL } from '@trimble/geospatial';
import { ILogger } from '@trimble/logging';
import { Accessor, createSignal } from 'solid-js';
import { drawingStyleForTool } from './feature-extraction-cad-config';

export interface CreateCadDrawingToolsParams {
  readonly viewer: Web3DViewer;
  readonly lineEditingPlugin: LineEditingPlugin;
  readonly model: LineEditingModel;
  readonly logger: ILogger;
}

export interface CadDrawingTools {
  readonly activeTool: Accessor<DrawingTool | undefined>;
  toggleTool(tool: DrawingTool): void;
  cancelActiveTool(): void;
  dispose(): void;
}

export function createCadDrawingTools(
  params: CreateCadDrawingToolsParams
): CadDrawingTools {
  const [activeTool, setActiveTool] = createSignal<DrawingTool | undefined>(
    undefined
  );
  // Prefer tool.enabled over viewer.activeTool (matches markup-capability).
  const getRegisteredTool = (name: string): Tool | undefined =>
    params.viewer.tools[name] as Tool | undefined;

  const toggleTool = (tool: DrawingTool): void => {
    if (activeTool() === tool) {
      cancelActiveTool();
      return;
    }
    params.lineEditingPlugin.setActiveModel(params.model);
    params.model.setDrawingStyle(drawingStyleForTool(tool));
    const nextTool = getRegisteredTool(tool);
    if (!nextTool) {
      params.logger.error(
        'Failed to activate line editing tool; it is not registered on the viewer toolManager',
        { tool }
      );
      return;
    }
    try {
      const previousTool = activeTool();
      const previousToolInstance = getRegisteredTool(
        previousTool ?? NO_ACTIVE_TOOL
      );
      if (previousToolInstance) {
        previousToolInstance.enabled = false;
      }
      nextTool.enabled = true;
      setActiveTool(tool);
      params.logger.info('Activated line editing tool', { tool });
    } catch (error) {
      params.logger.error(
        'Failed to activate line editing tool',
        { tool },
        error instanceof Error ? error : new Error(String(error))
      );
    }
  };

  const cancelActiveTool = (): void => {
    const previousTool = activeTool();
    if (previousTool === undefined) {
      return;
    }
    try {
      const previousToolInstance = getRegisteredTool(previousTool);
      const wasOwningInteraction = previousToolInstance?.enabled ?? false;
      if (previousToolInstance) {
        previousToolInstance.enabled = false;
      }
      if (wasOwningInteraction) {
        const selectionTool = getRegisteredTool(NO_ACTIVE_TOOL);
        if (selectionTool) {
          selectionTool.enabled = true;
        }
      }
    } catch (error) {
      params.logger.error(
        'Failed to deactivate line editing tool',
        { defaultTool: NO_ACTIVE_TOOL },
        error instanceof Error ? error : new Error(String(error))
      );
    }
    setActiveTool(undefined);
    params.logger.info('Cancelled line editing tool');
  };

  return {
    activeTool,
    toggleTool,
    cancelActiveTool,
    dispose: cancelActiveTool,
  };
}
